import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { CalendarDays, Search, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

// Source: https://www.lovska-zveza.si/prostozivece-zivali/lovne-dobe/
// Format: { vrsta, podvrsta, od: "DD.MM", do: "DD.MM" }
const SEASONS = [
  { vrsta: "Srna", podvrsta: "srnjak, lanščak", od: "01.05", do: "31.10" },
  { vrsta: "Srna", podvrsta: "srna, mladiči obeh spolov", od: "01.09", do: "31.12" },
  { vrsta: "Srna", podvrsta: "mladica", od: "01.05", do: "31.12" },
  { vrsta: "Navadni jelen", podvrsta: "jelen", od: "16.08", do: "31.12" },
  { vrsta: "Navadni jelen", podvrsta: "košuta", od: "01.09", do: "31.12" },
  { vrsta: "Navadni jelen", podvrsta: "teleta obeh spolov", od: "01.09", do: "31.01" },
  { vrsta: "Navadni jelen", podvrsta: "junica, lanščak", od: "01.07", do: "31.01" },
  { vrsta: "Damjak", podvrsta: "jelen", od: "16.08", do: "31.12" },
  { vrsta: "Damjak", podvrsta: "košuta", od: "01.09", do: "31.12" },
  { vrsta: "Damjak", podvrsta: "teleta obeh spolov", od: "01.09", do: "31.01" },
  { vrsta: "Damjak", podvrsta: "junica, lanščak", od: "01.07", do: "31.01" },
  { vrsta: "Muflon", podvrsta: "oven, lanščaki obeh spolov in jagnjeta obeh spolov", od: "01.08", do: "28.02" },
  { vrsta: "Muflon", podvrsta: "ovca", od: "01.08", do: "31.12" },
  { vrsta: "Gams", podvrsta: "kozel, koza, kozliči obeh spolov, enoletni obeh spolov", od: "01.08", do: "31.12" },
  { vrsta: "Alpski kozorog", podvrsta: "kozel, koza, kozliči obeh spolov, enoletni obeh spolov", od: "01.08", do: "31.12" },
  { vrsta: "Divji prašič", podvrsta: "merjasec", od: "01.01", do: "31.12" },
  { vrsta: "Divji prašič", podvrsta: "svinja", od: "01.07", do: "31.01" },
  { vrsta: "Divji prašič", podvrsta: "ozimci in lanščaki obeh spolov", od: "01.01", do: "31.12" },
  { vrsta: "Poljski zajec", podvrsta: null, od: "01.10", do: "15.12" },
  { vrsta: "Kuna belica, kuna zlatica", podvrsta: null, od: "01.11", do: "28.02" },
  { vrsta: "Jazbec", podvrsta: null, od: "01.08", do: "31.12" },
  { vrsta: "Lisica", podvrsta: null, od: "01.07", do: "15.03" },
  { vrsta: "Rakunasti pes", podvrsta: null, od: "01.08", do: "31.03" },
  { vrsta: "Šakal", podvrsta: null, od: "01.07", do: "15.03" },
  { vrsta: "Navadni polh", podvrsta: null, od: "01.10", do: "30.11" },
  { vrsta: "Alpski svizec", podvrsta: null, od: "01.09", do: "30.10" },
  { vrsta: "Pižmovka", podvrsta: null, od: "01.01", do: "31.12" },
  { vrsta: "Nutrija", podvrsta: null, od: "01.01", do: "31.12" },
  { vrsta: "Fazan", podvrsta: null, od: "01.09", do: "28.02" },
  { vrsta: "Poljska jerebica", podvrsta: "(gojena)", od: "01.09", do: "15.11" },
  { vrsta: "Raca mlakarica", podvrsta: null, od: "01.09", do: "15.01" },
  { vrsta: "Šoja", podvrsta: null, od: "01.08", do: "28.02" },
  { vrsta: "Sraka", podvrsta: null, od: "01.08", do: "28.02" },
  { vrsta: "Siva vrana", podvrsta: null, od: "01.08", do: "28.02" },
];

const MONTH_NAMES = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "avg", "sep", "okt", "nov", "dec"];

const parseDM = (s) => {
  const [d, m] = s.split(".").map(Number);
  return { day: d, month: m };
};

// Returns true if today is within season; supports wrap-around (e.g. 1.7. - 15.3.)
const isOpen = (od, do_) => {
  const now = new Date();
  const cur = (now.getMonth() + 1) * 100 + now.getDate(); // e.g. 1015 for 15.10
  const a = parseDM(od);
  const b = parseDM(do_);
  const start = a.month * 100 + a.day;
  const end = b.month * 100 + b.day;
  if (start <= end) {
    return cur >= start && cur <= end;
  }
  // wraps around year
  return cur >= start || cur <= end;
};

const formatRange = (od, do_) => {
  const a = parseDM(od);
  const b = parseDM(do_);
  return `${a.day}. ${MONTH_NAMES[a.month - 1]} – ${b.day}. ${MONTH_NAMES[b.month - 1]}`;
};

const HuntingSeasons = () => {
  const [search, setSearch] = useState("");
  const COLLAPSE_KEY = "hunting_seasons_collapsed_v1";
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      return raw === null ? true : raw === "1";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch { /* ignore */ }
  }, [collapsed]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return SEASONS;
    return SEASONS.filter(s =>
      s.vrsta.toLowerCase().includes(q) ||
      (s.podvrsta && s.podvrsta.toLowerCase().includes(q))
    );
  }, [search]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(s => {
      if (!map[s.vrsta]) map[s.vrsta] = [];
      map[s.vrsta].push(s);
    });
    return map;
  }, [filtered]);

  const openCount = SEASONS.filter(s => isOpen(s.od, s.do)).length;

  return (
    <Card data-testid="hunting-seasons-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-primary" />
            Lovne dobe divjadi
          </CardTitle>
          <div className="flex items-center gap-2">
            <a
              href="https://www.lovska-zveza.si/prostozivece-zivali/lovne-dobe/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1"
            >
              Vir LZS <ExternalLink className="h-3 w-3" />
            </a>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCollapsed(c => !c)}
              data-testid="seasons-toggle"
            >
              {collapsed ? <><ChevronDown className="h-4 w-4 mr-1" />Pokaži</> : <><ChevronUp className="h-4 w-4 mr-1" />Skrij</>}
            </Button>
          </div>
        </div>
      </CardHeader>
      {!collapsed && (
        <CardContent className="pt-0">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Išči vrsto divjadi..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
              data-testid="seasons-search"
            />
          </div>
          <ScrollArea className="h-[420px] pr-2">
            <div className="space-y-3">
              {Object.entries(grouped).map(([vrsta, items]) => (
                <div key={vrsta} className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 px-3 py-1.5 font-semibold text-sm">
                    {vrsta}
                  </div>
                  <div className="divide-y">
                    {items.map((s, idx) => {
                      const open = isOpen(s.od, s.do);
                      return (
                        <div
                          key={`${vrsta}-${idx}`}
                          className={`flex items-center justify-between px-3 py-2 gap-2 ${open ? "bg-green-50/60" : ""}`}
                        >
                          <div className="min-w-0">
                            {s.podvrsta && (
                              <div className="text-xs text-muted-foreground break-words">{s.podvrsta}</div>
                            )}
                            <div className="text-sm font-medium">{formatRange(s.od, s.do)}</div>
                          </div>
                          {open ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100 shrink-0">
                              ● Lovna doba
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground shrink-0">
                              Zaprto
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {Object.keys(grouped).length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-6">Ni rezultatov</p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      )}
    </Card>
  );
};

export default HuntingSeasons;
